/* eslint-disable @angular-eslint/no-input-rename */
/* eslint-disable @typescript-eslint/naming-convention */

import {
    AfterViewInit,
    ChangeDetectorRef,
    Directive,
    ElementRef,
    forwardRef,
    NgZone,
    OnDestroy,
    ViewContainerRef,
    input,
    inject,
    DOCUMENT
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { defer, fromEvent, merge, Observable, of, startWith, Subject, Subscription } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';

import { ApAutocompleteOptionComponent, ApAutocompleteOptionSelectionChange } from '../option/option.component';

import { ApAutocompleteComponent } from './autocomplete-panel.component';

/**
 * Provider that allows the autocomplete to register as a ControlValueAccessor.
 *
 * @docs-private
 */
export const AP_AUTOCOMPLETE_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => ApAutocompleteInputRefDirective),
    multi: true
};

/**
 * Creates an error to be thrown when attempting to use an autocomplete trigger without a panel.
 *
 * @docs-private
 */
export const getApAutocompleteMissingPanelError = (): Error =>
    Error(
        'Attempting to open an undefined instance of `ap-autocomplete`. ' +
            'Make sure that the id passed to the `apAutocomplete` is correct and that ' +
            `you're attempting to open it after the ngAfterContentInit hook.`
    );

@Directive({
    selector: 'input[apAutocompleteInputRef]',
    host: {
        class: 'ap-autocomplete-trigger',
        '[attr.role]': 'autocompleteDisabled() ? null : "combobox"',
        '[attr.aria-autocomplete]': 'autocompleteDisabled() ? null : "list"',
        '[attr.aria-activedescendant]': '(panelOpen && activeOption) ? activeOption.id() : null',
        '[attr.aria-expanded]': 'autocompleteDisabled() ? null : panelOpen.toString()',
        '[attr.aria-owns]': '(autocompleteDisabled() || !panelOpen) ? null : autocomplete()?.id',
        '[attr.aria-haspopup]': '!autocompleteDisabled()',
        '(focus)': '_handleFocus()',
        '(blur)': '_onTouched()',
        '(input)': '_handleInput($event)',
        '(keydown)': '_handleKeydown($event)'
    },
    providers: [AP_AUTOCOMPLETE_VALUE_ACCESSOR]
})
export class ApAutocompleteInputRefDirective implements ControlValueAccessor, AfterViewInit, OnDestroy {
    private readonly _element = inject<ElementRef<HTMLInputElement>>(ElementRef);
    private readonly _zone = inject(NgZone);
    private readonly _changeDetectorRef = inject(ChangeDetectorRef);
    private readonly _viewContainerRef = inject(ViewContainerRef);
    private readonly _document = inject(DOCUMENT, { optional: true });

    /** The panel element rendered into the DOM */
    private _panelElement: HTMLElement | null = null;

    /**
     * Whether the autocomplete is disabled. When disabled, the element will
     * act as a regular input and the user won't be able to open the panel.
     */
    public readonly autocompleteDisabled = input(false, {
        alias: 'apAutocompleteDisabled',
        transform: (value: boolean): boolean => coerceBooleanProperty(value)
    });

    /** Whether or not the autocomplete panel is open. */
    get panelOpen(): boolean {
        return this._panelAttached && this.autocomplete().showPanel;
    }

    /**
     * A stream of actions that should close the autocomplete panel, including
     * when an option is selected, on blur, and when TAB is pressed.
     */
    get panelClosingActions(): Observable<ApAutocompleteOptionSelectionChange | null> {
        return merge(
            this.optionSelections,
            this.autocomplete()._keyManager.tabOut.pipe(filter(() => this._panelAttached)),
            this.closeKeyEventStream,
            this._getOutsideClickStream(),
            of()
        ).pipe(map((event) => (event instanceof ApAutocompleteOptionSelectionChange ? event : null)));
    }

    /** The currently active option, coerced to ApAutocompleteOptionComponent type. */
    get activeOption(): ApAutocompleteOptionComponent | null {
        const autocomplete = this.autocomplete();
        if (autocomplete && autocomplete._keyManager) {
            return autocomplete._keyManager.activeItem;
        }
        return null;
    }

    /** The autocomplete panel to be attached to this trigger. */
    readonly autocomplete = input<ApAutocompleteComponent>(undefined, { alias: 'apAutocomplete' });

    /** Element to connect to. */
    readonly connectedElement = input<ElementRef<HTMLElement>>(undefined);

    /**
     * Position of the autocomplete panel relative to the trigger element. A position of `auto`
     * will render the panel underneath the trigger if there is enough space for it to fit in
     * the viewport, otherwise the panel will be shown above it. If the position is set to
     * `above` or `below`, the panel will always be shown above or below the trigger. no matter
     * whether it fits completely in the viewport.
     */
    readonly position = input<'auto' | 'above' | 'below'>('auto', { alias: 'apAutocompletePosition' });

    /** Stream of autocomplete option selections. */
    readonly optionSelections: Observable<ApAutocompleteOptionSelectionChange> = defer(() => {
        const autocomplete = this.autocomplete();
        if (autocomplete && autocomplete.options) {
            return autocomplete.options.changes.pipe(
                startWith(autocomplete.options),
                switchMap((options) => merge(...options.map((option) => option.selectionChange)))
            ) as Observable<ApAutocompleteOptionSelectionChange>;
        }

        // If there are any subscribers before `ngAfterViewInit`, the `autocomplete` will be undefined.
        // Return a stream that we'll replace with the real one once everything is in place.
        return this._zone.onStable.asObservable().pipe(
            take(1),
            switchMap(() => this.optionSelections)
        );
    });

    private _componentDestroyed = false;
    private _panelAttached = false;

    /** Old value of the native input. Used to work around issues with the `input` event on IE. */
    private _previousValue: string | number | null;

    /** Whether or not the label state is being overridden. */
    private _manuallyFloatingLabel = false;

    /** The subscription for closing actions (some are bound to document). */
    private _closingActionsSubscription: Subscription;

    /**
     * Whether the autocomplete can open the next time it is focused. Used to prevent a focused,
     * closed autocomplete from being reopened if the user switches to another browser tab and then
     * comes back.
     */
    private _canOpenOnNextFocus = true;

    /** Whether the element is inside of a ShadowRoot component. */
    private readonly _isInsideShadowRoot = false;

    /** Stream of keyboard events that can close the panel. */
    private readonly closeKeyEventStream = new Subject<void>();

    /** `View -> model callback called when value changes` */
    public _onChange: (value: any) => void = () => {};

    /** `View -> model callback called when autocomplete has been touched` */
    public _onTouched = () => {};

    public ngAfterViewInit(): void {
        if (typeof window !== 'undefined') {
            this._zone.runOutsideAngular(() => {
                window.addEventListener('blur', this._windowBlurHandler);
            });
        }
    }

    public ngOnDestroy(): void {
        if (typeof window !== 'undefined') {
            window.removeEventListener('blur', this._windowBlurHandler);
        }

        this._componentDestroyed = true;
        this._destroyPanel();
        this.closeKeyEventStream.complete();
    }

    public openPanel(): void {
        this._attachPanel();
    }

    public closePanel(): void {
        this._resetLabel();

        if (!this._panelAttached) {
            return;
        }

        this.autocomplete()._isOpen = false;
        this._panelAttached = false;

        if (this._panelElement) {
            this._panelElement.style.display = 'none';
            this._closingActionsSubscription?.unsubscribe();
        }

        // Note that in some cases this can end up being called after the component is destroyed.
        // Add a check to ensure that we don't try to run change detection on a destroyed view.
        if (!this._componentDestroyed) {
            // We need to trigger change detection manually, because
            // `fromEvent` doesn't seem to do it at the proper time.
            // This ensures that the label is reset when the
            // user clicks outside.
            this._changeDetectorRef.detectChanges();
        }
    }

    public clearValue(): void {
        this.writeValue('');
        this._onChange('');
    }

    // Implemented as part of ControlValueAccessor.
    public writeValue(value: any): void {
        Promise.resolve(null).then(() => this._setTriggerValue(value));
    }

    // Implemented as part of ControlValueAccessor.
    public registerOnChange(fn: (value: any) => unknown): void {
        this._onChange = fn;
    }

    // Implemented as part of ControlValueAccessor.
    public registerOnTouched(fn: () => unknown): void {
        this._onTouched = fn;
    }

    // Implemented as part of ControlValueAccessor.
    public setDisabledState(isDisabled: boolean): void {
        this._element.nativeElement.disabled = isDisabled;
    }

    public _handleKeydown(event: KeyboardEvent): void {
        const code = event.code;
        const autocomplete = this.autocomplete();

        if (this.activeOption && code === 'Enter' && this.panelOpen) {
            this.activeOption.selectViaInteraction();
            this._resetActiveItem();
            event.preventDefault();
        } else if (autocomplete) {
            const isArrowKey = code === 'ArrowUp' || code === 'ArrowDown';

            if (this.panelOpen && code === 'Escape') {
                this.closePanel();
            } else if (this.panelOpen || code === 'Tab') {
                autocomplete._keyManager.onKeydown(event);
            } else if (isArrowKey && this._canOpen()) {
                this.openPanel();
            }
        }
    }

    public _handleInput(event: any): void {
        const target = event.target as HTMLInputElement;
        let value: number | string | null = target.value;

        // Based on `NumberValueAccessor` from forms.
        if (target.type === 'number') {
            value = value === '' ? null : parseFloat(value);
        }

        // If the input has a placeholder, IE will fire the `input` event on page load,
        // focus and blur, in addition to when the user actually changed the value. To
        // filter out all of the extra events, we save the value on focus and between
        // `input` events, and we check whether it changed.
        // See: https://connect.microsoft.com/IE/feedback/details/885747/
        if (this._previousValue !== value) {
            this._previousValue = value;
            this._onChange(value);
            if (this._canOpen() && this._document.activeElement === event.target) {
                this.openPanel();
            }
        }
    }

    public _handleFocus(): void {
        if (!this._canOpenOnNextFocus) {
            this._canOpenOnNextFocus = true;
            this.blur();
        } else if (this._canOpen()) {
            // element value might not be set yet
            requestAnimationFrame(() => (this._previousValue = this._element.nativeElement.value)); // todo: remove log
            this.openPanel();
        }
    }

    /**
     * Event handler for when the window is blurred. Needs to be an
     * arrow function in order to preserve the context.
     */
    private readonly _windowBlurHandler = () => {
        // If the user blurred the window while the autocomplete is focused, it means that it'll be
        // refocused when they come back. In this case we want to skip the first focus event, if the
        // pane was closed, in order to avoid reopening it unintentionally.
        this._canOpenOnNextFocus = this._document.activeElement !== this._element.nativeElement || this.panelOpen;
    };

    /** Stream of clicks outside of the autocomplete panel. */
    private _getOutsideClickStream(): Observable<any> {
        return merge(fromEvent(this._document, 'click'), fromEvent(this._document, 'touchend')).pipe(
            filter((event: UIEvent) => {
                // If we're in the Shadow DOM, the event target will be the shadow root, so we have to
                // fall back to check the first element in the path of the click event.
                const clickTarget = (
                    this._isInsideShadowRoot && event.composedPath ? event.composedPath()[0] : event.target
                ) as HTMLElement;

                return (
                    this._panelAttached &&
                    clickTarget !== this._element.nativeElement &&
                    !!this._panelElement &&
                    !this._panelElement.contains(clickTarget)
                );
            })
        );
    }

    /** If the label has been manually elevated, return it to its normal state. */
    private _resetLabel(): void {
        if (this._manuallyFloatingLabel) {
            this._manuallyFloatingLabel = false;
        }
    }

    private _subscribeToClosingActions(): Subscription {
        return this.panelClosingActions.pipe(take(1)).subscribe((event) => {
            this._resetActiveItem();
            this.autocomplete()._setVisibility();
            this._setValueAndClose(event);
        });
    }

    /** Destroys the autocomplete suggestion panel. */
    private _destroyPanel(): void {
        if (this._panelElement) {
            this.closePanel();
            this._panelElement.remove();
            this._panelElement = null;
        }
    }

    private _setTriggerValue(value: any): void {
        const toDisplay = value;

        // Simply falling back to an empty string if the display value is falsy does not work properly.
        // The display value can also be the number zero and shouldn't fall back to an empty string.
        const inputValue = toDisplay != null ? toDisplay : '';

        // If it's used within a `MatFormField`, we should set it through the property so it can go
        // through change detection.
        this._element.nativeElement.value = inputValue;
        this._previousValue = inputValue;
    }

    /**
     * This method closes the panel, and if a value is specified, also sets the associated
     * control to that value. It will also mark the control as dirty if this interaction
     * stemmed from the user.
     */
    private _setValueAndClose(event: ApAutocompleteOptionSelectionChange | null): void {
        if (event && event.source) {
            this._clearPreviousSelectedOption(event.source);

            const value = event.source.value();
            this._onChange(value);
            this._previousValue = value;

            this._element.nativeElement.focus();
            this.autocomplete()._emitSelectEvent(event.source);
        }

        this.closePanel();
    }

    /**
     * Clear any previous selected option and emit a selection change event for this option
     */
    private _clearPreviousSelectedOption(skip: ApAutocompleteOptionComponent): void {
        this.autocomplete().options.forEach((option) => {
            if (option !== skip && option.selected()) {
                option.deselect();
            }
        });
    }

    private _attachPanel(): void {
        const autocomplete = this.autocomplete();
        if (!autocomplete) {
            throw getApAutocompleteMissingPanelError();
        }

        if (!this._panelElement) {
            // Render the autocomplete template into the view
            const embeddedView = this._viewContainerRef.createEmbeddedView(autocomplete.template);
            embeddedView.detectChanges();

            // Grab the root element of the rendered template
            this._panelElement = embeddedView.rootNodes[0] as HTMLElement;

            // Insert the panel directly after the connectedElement in the DOM
            this.connectedElement().nativeElement.insertAdjacentElement('afterend', this._panelElement);

            // Handle keyboard events on the panel for accessibility
            this._panelElement.addEventListener('keydown', (event: KeyboardEvent) => {
                if (event.code === 'ArrowUp' && event.altKey) {
                    this._resetActiveItem();
                    this.closeKeyEventStream.next();
                    event.stopPropagation();
                    event.preventDefault();
                    this.blur();
                } else if (event.code === 'Escape') {
                    this.closeKeyEventStream.next();
                }
            });
        }

        // Show the panel
        this._panelElement.style.display = '';

        // Always recreate subscription when panel opens
        this._closingActionsSubscription?.unsubscribe();
        this._closingActionsSubscription = this._subscribeToClosingActions();

        autocomplete._setVisibility();
        autocomplete._isOpen = true;
        this._panelAttached = true;
    }

    /**
     * Resets the active item to -1 so arrow events will activate the
     * correct options, or to 0 if the consumer opted into it.
     */
    private _resetActiveItem(): void {
        this.autocomplete()._keyManager.setActiveItem(this.autocomplete().autoActiveFirstOption ? 0 : -1);
    }

    /** Determines whether the panel can be opened. */
    private _canOpen(): boolean {
        const element = this._element.nativeElement;
        return !element.readOnly && !element.disabled && !this.autocompleteDisabled();
    }

    private blur(): void {
        this._element.nativeElement.blur();
    }
}

function coerceBooleanProperty(value: boolean): boolean {
    return value != null && `${value}` !== 'false';
}
